/**
 * Observes visibility of elements matching a selector using MutationObserver and IntersectionObserver.
 */
export class VisibilityObserver {
    /**
     * @param callback The callback function to be executed when an element becomes visible.
     */
    constructor(callback) {
        this.callback = callback;
    }
    /**
     * Tracks visibility of elements matching {@link selector}, triggering {@link callback} when an element is over 2/3 visible.
     * Uses {@link https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver | MutationObserver} to monitor the DOM for dynamically loaded content.
     * Callback is invoked only once per element.
     * @param selector The CSS selector for elements to track.
     * @throws Error if called more than once.
     */
    observe(selector) {
        if (this.selector) {
            throw new Error(`Cannot observe selector ${selector}. A visibility observer has already been set up for selector ${this.selector}.`);
        }
        this.selector = selector;
        this.setupObservers();
        this.elements().forEach((elementToTrack) => this.observeIntersection(elementToTrack));
    }
    /**
     * @returns {HTMLElement[]} An array of elements that match the selector in the document.
     */
    elements() {
        return Array.from(document.querySelectorAll(this.selector));
    }
    // Initialize both observers and start watching for DOM changes
    setupObservers() {
        this.setupIntersectionObserver();
        this.setupMutationObserver();
        this.observeMutations();
    }
    // Create observer to detect when elements become visible in the viewport
    setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const node = entry.target;
                    this.callback(node);
                    this.intersectionObserver.unobserve(node);
                }
            });
        }, { threshold: 2 / 3 });
    }
    // Create observer to detect DOM changes that may add/remove tracked elements
    setupMutationObserver() {
        this.mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                this.handleAddedNodes(mutation.addedNodes);
                this.handleRemovedNodes(mutation.removedNodes);
            });
        });
    }
    handleAddedNodes(nodes) {
        this.processHtmlNodes(nodes, (element) => this.observeIntersection(element));
    }
    handleRemovedNodes(nodes) {
        this.processHtmlNodes(nodes, (element) => this.intersectionObserver.unobserve(element));
    }
    processHtmlNodes(nodes, action) {
        nodes.forEach((node) => {
            if (node instanceof HTMLElement &&
                this.nodeMatchesOrContainsSelector(node)) {
                this.processMatchingElements(node, action);
            }
        });
    }
    processMatchingElements(node, action) {
        const elementsToTrack = this.extractElementsToTrack(node);
        elementsToTrack.forEach(action);
    }
    nodeMatchesOrContainsSelector(node) {
        return (node.matches(this.selector) || node.querySelector(this.selector) !== null);
    }
    // Get matching descendants and the node itself if it matches the selector
    extractElementsToTrack(node) {
        const elements = Array.from(node.querySelectorAll(this.selector));
        if (node.matches(this.selector)) {
            elements.push(node);
        }
        return elements;
    }
    observeIntersection(node) {
        this.intersectionObserver.observe(node);
    }
    observeMutations() {
        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
}
//# sourceMappingURL=visibility-observer.js.map